import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const ACCENT_COLORS = ['purple', 'blue', 'cyan', 'green', 'orange', 'pink', 'red'];

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  const [accentColor, setAccentColorState] = useState(() => {
    return localStorage.getItem('accentColor') || 'purple';
  });

  const [resolvedTheme, setResolvedTheme] = useState('dark');

  // Apply theme to DOM
  const applyTheme = useCallback((newTheme) => {
    let resolved = newTheme;
    if (newTheme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      resolved = prefersDark ? 'dark' : 'light';
    }
    setResolvedTheme(resolved);

    const html = document.documentElement;
    html.classList.remove('light', 'dark');
    html.classList.add(resolved);
  }, []);

  // Apply accent color to DOM
  const applyAccentColor = useCallback((newColor) => {
    const html = document.documentElement;
    ACCENT_COLORS.forEach(color => {
      html.classList.remove(`accent-${color}`);
    });
    html.classList.add(`accent-${newColor}`);
  }, []);

  // Initial theme application
  useEffect(() => {
    applyTheme(theme);
    applyAccentColor(accentColor);
  }, [theme, accentColor, applyTheme, applyAccentColor]);

  // Listen for system theme changes when in system mode
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e) => {
        const newTheme = e.matches ? 'dark' : 'light';
        setResolvedTheme(newTheme);
        document.documentElement.classList.remove('light', 'dark');
        document.documentElement.classList.add(newTheme);
      };
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [theme]);

  // Listen for localStorage changes (from other components updating preferences)
  useEffect(() => {
    const handleStorageChange = () => {
      const storedTheme = localStorage.getItem('theme');
      const storedAccent = localStorage.getItem('accentColor');
      
      if (storedTheme && storedTheme !== theme) {
        setThemeState(storedTheme);
        applyTheme(storedTheme);
      }
      if (storedAccent && storedAccent !== accentColor) {
        setAccentColorState(storedAccent);
        applyAccentColor(storedAccent);
      }
    };

    // Poll localStorage periodically to catch updates from AuthContext
    const interval = setInterval(handleStorageChange, 500);
    
    // Also listen for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [theme, accentColor, applyTheme, applyAccentColor]);

  const setTheme = (newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
    applyTheme(newTheme);
  };

  const setAccentColor = (newColor) => {
    if (ACCENT_COLORS.includes(newColor)) {
      setAccentColorState(newColor);
      localStorage.setItem('accentColor', newColor);
      applyAccentColor(newColor);
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      setTheme, 
      resolvedTheme, 
      accentColor, 
      setAccentColor,
      accentColors: ACCENT_COLORS 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
